import React from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, Copy, KeyRound, Link2, X } from 'lucide-react';
import { llmRouteBaseUrl, llmRouteParameterizedUrl } from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';
import { ghostBtn, labelClass, panelClass } from '../components/GatewayUi';

function CopyBlock({
  label,
  value,
  isLight,
  onCopy,
}: {
  label: string;
  value: string;
  isLight: boolean;
  onCopy: (value: string, label: string) => void;
}) {
  return (
    <div>
      <p className={labelClass(isLight)}>{label}</p>
      <div className="flex gap-2 items-start">
        <pre
          className={`flex-1 min-w-0 rounded-xl border px-3 py-2.5 text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-all max-h-48 overflow-y-auto ${
            isLight ? 'border-neutral-200 bg-white text-neutral-800' : 'border-neutral-700 bg-neutral-900 text-neutral-200'
          }`}
        >
          {value || '—'}
        </pre>
        <button type="button" className={ghostBtn(isLight)} disabled={!value} onClick={() => onCopy(value, label)}>
          <Copy size={14} />
        </button>
      </div>
    </div>
  );
}

export default function LlmRouteConfigureDialog({
  open,
  isLight,
  gatewayId,
  gatewayAlias,
  tenantId,
  clientId,
  token,
  onClose,
  onCopy,
}: {
  open: boolean;
  isLight: boolean;
  gatewayId: string;
  gatewayAlias: string;
  tenantId: string;
  clientId: string;
  token: string;
  onClose: () => void;
  onCopy: (value: string, label: string) => void;
}) {
  if (!open) return null;

  const baseUrl = gatewayId ? llmRouteBaseUrl(gatewayId) : '';
  const parameterizedUrl = gatewayId ? llmRouteParameterizedUrl(gatewayId, tenantId, clientId) : '';
  const model = gatewayAlias || 'your-gateway-alias';
  const shownToken = token || '<llm-route-token>';

  const parameterizedConfig = [
    `OPENAI_BASE_URL=${baseUrl || 'https://gateway.selfic.ai/gw/<gateway-id>/v1'}`,
    `OPENAI_MODEL=${model}`,
    `SELFIC_TENANT_ID=${tenantId || '<tenant_id>'}`,
    `SELFIC_CLIENT_ID=${clientId || '<client_id>'}`,
  ].join('\n');

  const completionsQuery = new URLSearchParams();
  if (tenantId) completionsQuery.set('tenant_id', tenantId);
  if (clientId) completionsQuery.set('client_id', clientId);
  const parameterizedCompletions = baseUrl
    ? `${baseUrl}/chat/completions${completionsQuery.toString() ? `?${completionsQuery.toString()}` : ''}`
    : '';

  const parameterizedExample = `curl "${parameterizedCompletions || 'https://gateway.selfic.ai/gw/<gateway-id>/v1/chat/completions'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model}",
    "messages": [{"role": "user", "content": "Summarize this quarter"}]
  }'`;

  const bearerConfig = [
    `OPENAI_BASE_URL=${baseUrl || 'https://gateway.selfic.ai/gw/<gateway-id>/v1'}`,
    `OPENAI_MODEL=${model}`,
    `OPENAI_API_KEY=${shownToken}`,
  ].join('\n');

  const bearerExample = `curl "${baseUrl || 'https://gateway.selfic.ai/gw/<gateway-id>/v1'}/chat/completions" \\
  -H "Authorization: Bearer ${shownToken}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model}",
    "messages": [{"role": "user", "content": "Summarize this quarter"}]
  }'`;

  const copy = (value: string, label: string) => {
    logGovernedMcp('app', 'llm_route_configure_copied', { label });
    onCopy(value, label);
  };

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const title = isLight ? 'text-neutral-900' : 'text-white';

  return createPortal(
    <div className="fixed inset-0 z-[230] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[3px]" aria-label="Close" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border shadow-2xl flex flex-col ${
          isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-950'
        }`}
      >
        <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'
              }`}
            >
              <BookOpen size={18} />
            </div>
            <div>
              <h3 className={`text-base font-semibold tracking-tight ${title}`}>Configure the LLM gateway route</h3>
              <p className={`text-sm mt-1.5 leading-relaxed ${muted}`}>
                Call this tenant gateway with either query parameters or the signed JWT. Both identify the same custom client.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1.5 rounded-lg ${isLight ? 'hover:bg-neutral-100 text-neutral-400' : 'hover:bg-neutral-800 text-neutral-500'}`}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pb-5 overflow-y-auto space-y-4">
          <section className={`${panelClass(isLight)} p-4`}>
            <div className="flex items-start gap-3">
              <Link2 size={16} className="text-indigo-500 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <h4 className={`text-sm font-semibold ${title}`}>Parameterized</h4>
                <p className={`text-xs mt-1 leading-relaxed ${muted}`}>
                  Send <span className="font-mono">tenant_id</span> and <span className="font-mono">client_id</span> on the
                  OpenAI-compatible base URL. Use the gateway alias as the model name. No Authorization header is required.
                </p>
                <div className="mt-3 space-y-3">
                  <CopyBlock label="Base URL with tenant and client" value={parameterizedUrl} isLight={isLight} onCopy={copy} />
                  <CopyBlock label="Environment" value={parameterizedConfig} isLight={isLight} onCopy={copy} />
                  <CopyBlock label="Example request" value={parameterizedExample} isLight={isLight} onCopy={copy} />
                </div>
              </div>
            </div>
          </section>

          <section className={`${panelClass(isLight)} p-4`}>
            <div className="flex items-start gap-3">
              <KeyRound size={16} className="text-indigo-500 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <h4 className={`text-sm font-semibold ${title}`}>JWT bearer token</h4>
                <p className={`text-xs mt-1 leading-relaxed ${muted}`}>
                  Keep the base URL clean and send the route JWT as <span className="font-mono">Authorization: Bearer</span>.
                  Claims already carry tenant, client, and gateway so the request stays scoped.
                </p>
                <div className="mt-3 space-y-3">
                  <CopyBlock label="Authorization header" value={token ? `Bearer ${token}` : ''} isLight={isLight} onCopy={copy} />
                  <CopyBlock label="Environment" value={bearerConfig} isLight={isLight} onCopy={copy} />
                  <CopyBlock label="Example request" value={bearerExample} isLight={isLight} onCopy={copy} />
                </div>
              </div>
            </div>
          </section>
        </div>

        <div
          className={`px-5 py-3.5 flex justify-end border-t ${
            isLight ? 'border-neutral-100 bg-neutral-50/80' : 'border-neutral-800 bg-neutral-900/80'
          }`}
        >
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
